import { TUTORIAL_GIVEN } from '../tutorial.js';
/** The camp's townsfolk (ADR 0066): their zone sets them apart from Havrebourg's. */
const TUTOR = { mob: 'tutor', biome: 'meadow', zone: 'tutorial' };
const MERCHANT = { mob: 'merchant', biome: 'meadow', zone: 'tutorial' };
const SMELTER = { mob: 'smelter', biome: 'meadow', zone: 'tutorial' };
const SMITH = { mob: 'smith', biome: 'meadow', zone: 'tutorial' };
const CAPTAIN = { mob: 'captain', biome: 'meadow' };
/**
 * The Camp des premiers pas (ADR 0030, ADR 0066, ADR 0078): the introduction
 * as a chain of four quests, like everything after it. Hunt and pick up,
 * sell and buy and wear, melt five swords into a rare one and take it to
 * +1, then the road to Havrebourg, where the captain takes the last one back
 * and the meadow's chain begins.
 *
 * The story (ADR 0067, `docs/LORE.md`): the camp is the Givrecoeur Company's
 * recruiting camp. Ser Givrecoeur saw the ice crack under Givrecime and a red
 * glow beneath it; Havrebourg laughed, so he trains his recruits himself. The
 * merchant, the smelter and the smith are its first members, named in their
 * own lines: their nameplates stay their trades, which the client opens its
 * windows by. Each lesson is said plainly, with the very click it asks; the
 * story stands around it, never in its place.
 *
 * No quest of the camp pays gold: the rats' pelts pay for the path to the
 * coin (`tutorialPeltsDue`). The merchant hands over the breastplate and the
 * buckler, the smelter the five swords and the smith's flint, with their
 * lessons (`grants`, bound), never as a reward that could be sold on the way: the camp's merchant buys none of the lessons' pieces
 * (`campKeeps`), and a lesson is never abandoned. The lessons bring a
 * newcomer to level 2 as the smelter's is turned in.
 */
const QUESTS = [
    {
        id: 'camp-rats',
        name: 'Premiers coups',
        biome: 'meadow',
        prologue: true,
        level: 1,
        giver: TUTOR,
        turnIn: TUTOR,
        offer: 'Une nouvelle recrue ! Va aux rats du terrain d’entraînement, au sud-est : clique sur un rat pour le frapper, puis sur ce qu’il laisse pour le ramasser.',
        summary: 'Chasser trois rats sur le terrain d’entraînement et ramasser leurs peaux.',
        pending: 'Trois rats, au sud-est, la flèche te montre le chemin. Ramasse bien leurs peaux. Et si un rat te met par terre, tu te relèves ici sans rien perdre : au camp, on apprend.',
        complete: 'Pas mal du tout ! Il y a trois hivers, j’ai vu de la lumière rouge sous la glace de Givrecime, et tout Havrebourg s’est moqué de moi, alors je forme mes recrues moi-même. Va vendre tes peaux à Odon, à l’étal. Il est un peu radin, mais c’est le premier qui a cru en moi.',
        objectives: [{ kind: 'kill', label: 'Rats chassés', mobs: ['rat'], count: 3 }],
        reward: { xp: 20, gold: 0 },
    },
    {
        id: 'camp-armour',
        name: 'Le prix d’une armure',
        biome: 'meadow',
        prologue: true,
        level: 1,
        requires: ['camp-rats'],
        giver: MERCHANT,
        turnIn: MERCHANT,
        offer: 'Salut, moi c’est Odon. Tiens, un plastron et une rondache, offerts : ça ne m’arrive pas souvent. Vends-moi tes peaux, achète le casque, les gantelets et les sabots, puis enfile le tout. On ne part pas chasser le dragon en chemise de nuit !',
        summary: 'Vendre les peaux à Odon, acheter le casque, les gantelets et les sabots, puis porter les cinq pièces.',
        pending: 'Mes affaires, c’est le bouton sous mes mots. Onglet Vendre pour tes peaux, onglet Acheter pour les trois pièces. Ensuite, ouvre ton sac et enfile tout, mes cadeaux compris. S’il te manque de l’or, les rats en ont encore.',
        complete: 'Là, tu ressembles à quelque chose. Va voir Garin, le fondeur, au bout du camp. Il a des épées en trop, comme d’habitude.',
        objectives: [
            { kind: 'wear', label: 'Porter le Casque d’écorce', items: ['bark-helm'] },
            { kind: 'wear', label: 'Porter le Plastron d’écorce', items: ['bark-chest'] },
            { kind: 'wear', label: 'Porter les Gantelets en bois', items: ['wooden-gloves'] },
            { kind: 'wear', label: 'Porter les Sabots en bois', items: ['wooden-boots'] },
            { kind: 'wear', label: 'Porter la Rondache en bois', items: ['wooden-shield'] },
        ],
        grants: TUTORIAL_GIVEN.map((templateId) => ({ templateId, quantity: 1 })),
        reward: { xp: 25, gold: 0 },
    },
    {
        id: 'camp-fusion',
        name: 'Cinq lames en une',
        biome: 'meadow',
        prologue: true,
        level: 1,
        requires: ['camp-armour'],
        giver: SMELTER,
        turnIn: SMITH,
        offer: 'J’ai fondu les cloches de Havrebourg pendant vingt ans, et maintenant je fonds des épées en bois. La vie, hein ! Tiens, en voilà cinq, et un silex pour Hald. Je fonds cinq objets identiques en un seul, plus solide. Fonds-les chez moi, prends la nouvelle en main, puis montre-la à Hald, il va râler.',
        summary: 'Fondre les cinq épées chez Garin, porter l’épée rare, puis la faire renforcer à +1 chez Hald, le forgeron.',
        pending: 'Garin d’abord, moi ensuite, comme toujours. Il fond tes cinq épées en une, tu la prends en main, et tu me l’apportes avec le silex. Le premier cran réussit à tous les coups.',
        complete: 'Et voilà, plus un ! Mieux que tout ce que Garin a jamais fondu. Tu as tout appris, l’arche est ouverte pour toi. Passe voir le vieux avant de partir, il a une lettre pour toi. Il la réécrit depuis ce matin.',
        objectives: [
            {
                kind: 'wear',
                label: 'Fondre et porter l’Épée en bois rare',
                items: ['wooden-sword-rare'],
                at: SMELTER,
            },
            {
                kind: 'upgrade',
                label: 'L’épée rare renforcée à +1',
                level: 1,
                items: ['wooden-sword-rare'],
                at: SMITH,
            },
        ],
        grants: [
            { templateId: 'wooden-sword', quantity: 5 },
            { templateId: 'flint', quantity: 1 },
        ],
        reward: { xp: 55, gold: 0 },
    },
    {
        id: 'camp-road',
        name: 'La lettre au capitaine',
        biome: 'meadow',
        prologue: true,
        level: 1,
        requires: ['camp-fusion'],
        giver: TUTOR,
        turnIn: CAPTAIN,
        offer: 'Te voilà membre de la Compagnie ! Porte cette lettre au capitaine Aldric, sur la place de Havrebourg. L’arche, à l’est, t’y emmène. Il m’a mis à la porte de sa ville il y a deux hivers, mais je lui demande quand même un petit coin pour nous installer. Et retiens notre devise : « Tant que le givre tient. » Je l’ai trouvée tout seul.',
        summary: 'Passer l’arche à l’est du camp et porter la lettre de Ser Givrecoeur au capitaine Aldric, à Havrebourg.',
        pending: 'La place est juste de l’autre côté de l’arche. Aldric, c’est celui qui crie.',
        complete: 'Une lettre de Givrecoeur… Trois pages pour me demander un coin de rue, et deux pages sur son dragon. Bon, d’accord. Qu’il prenne la cour au nord de la place, personne n’en veut. Mais qu’il ne vienne pas me parler de dragons tous les matins.',
        objectives: [{ kind: 'reach', label: 'Atteindre Havrebourg', landmark: 'Havrebourg' }],
        reward: { xp: 20, gold: 2 },
    },
];
export const CAMP = {
    quests: QUESTS,
    npcs: {},
    keepers: [],
    items: [],
    xp: {},
    greetings: {},
};
//# sourceMappingURL=camp.js.map